import { useState } from 'react';
import { 
  Plus, Edit2, Trash2, Tag, Search, Hash, 
  Save 
} from 'lucide-react';
import { Button, Input, Modal, Card } from '../common';
import { useTagManagement } from '../../hooks/useTagManagement';
import type { ReadingTag, TagCategory } from '../../types/collections';
import type { TarotReading } from '../../types/tarot';

interface TagManagerProps {
  readings: TarotReading[];
  isOpen: boolean;
  onClose: () => void;
}

const colorOptions = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
  '#ec4899', '#f43f5e', '#64748b', '#6b7280', '#374151'
];

export default function TagManager({ readings, isOpen, onClose }: TagManagerProps) {
  const {
    tags,
    categories,
    createTag,
    updateTag,
    deleteTag,
    createCategory,
    updateCategory,
    deleteCategory,
    getTagsByCategory: _getTagsByCategory,
    getPopularTags,
    searchTags
  } = useTagManagement(readings);

  const [activeTab, setActiveTab] = useState<'tags' | 'categories'>('tags');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTag, setEditingTag] = useState<ReadingTag | null>(null);
  const [editingCategory, setEditingCategory] = useState<TagCategory | null>(null);
  const [showCreateTag, setShowCreateTag] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'tag' | 'category'; id: string } | null>(null);

  const [newTag, setNewTag] = useState({
    name: '',
    color: colorOptions[0],
    description: ''
  });

  const [newCategory, setNewCategory] = useState({
    name: '',
    color: colorOptions[0],
    tags: [] as string[]
  });

  const filteredTags = searchQuery ? searchTags(searchQuery) : tags;
  const popularTags = getPopularTags(5);

  const handleCreateTag = () => {
    if (newTag.name.trim()) {
      createTag({
        name: newTag.name.trim().toLowerCase(),
        color: newTag.color,
        description: newTag.description.trim() || undefined
      });
      setNewTag({ name: '', color: colorOptions[0], description: '' });
      setShowCreateTag(false);
    }
  };

  const handleUpdateTag = () => {
    if (editingTag) {
      updateTag(editingTag.id, {
        name: editingTag.name.trim().toLowerCase(),
        color: editingTag.color,
        description: editingTag.description?.trim() || undefined
      });
      setEditingTag(null);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCreateCategory = () => {
    if (newCategory.name.trim()) {
      createCategory({
        name: newCategory.name.trim(),
        color: newCategory.color,
        tags: newCategory.tags
      });
      setNewCategory({ name: '', color: colorOptions[0], tags: [] });
      setShowCreateCategory(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUpdateCategory = () => {
    if (editingCategory) {
      updateCategory(editingCategory.id, {
        name: editingCategory.name.trim(),
        color: editingCategory.color,
        tags: editingCategory.tags
      });
      setEditingCategory(null);
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      if (deleteConfirm.type === 'tag') {
        deleteTag(deleteConfirm.id);
      } else {
        deleteCategory(deleteConfirm.id);
      }
      setDeleteConfirm(null);
    }
  };

  const ColorPicker = ({ 
    value, 
    onChange 
  }: { 
    value: string; 
    onChange: (color: string) => void; 
  }) => (
    <div className="grid grid-cols-10 gap-1">
      {colorOptions.map(color => (
        <button
          key={color}
          onClick={() => onChange(color)}
          className={`w-6 h-6 rounded-full border-2 transition-all ${
            value === color ? 'border-white scale-110' : 'border-transparent'
          }`}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Tag Manager"
      size="lg"
    >
      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex border-b border-dark-700">
          <button
            onClick={() => setActiveTab('tags')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'tags'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Tag className="h-4 w-4 inline mr-2" />
            Tags ({tags.length})
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'categories'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Hash className="h-4 w-4 inline mr-2" />
            Categories ({categories.length})
          </button>
        </div>

        {/* Search and Actions */}
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={`Search ${activeTab}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            onClick={() => {
              if (activeTab === 'tags') {
                setShowCreateTag(true);
              } else {
                setShowCreateCategory(true);
              }
            }}
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add {activeTab === 'tags' ? 'Tag' : 'Category'}
          </Button>
        </div>

        {/* Tags Tab */}
        {activeTab === 'tags' && (
          <div className="space-y-4">
            {/* Popular Tags */}
            {!searchQuery && popularTags.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Most Used</h4>
                <div className="flex flex-wrap gap-2">
                  {popularTags.map(tag => (
                    <div
                      key={tag.id}
                      className="flex items-center gap-2 px-3 py-1 rounded-full text-sm"
                      style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                    >
                      <span>{tag.name}</span>
                      <span className="text-xs opacity-70">({tag.usageCount})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Tags */}
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-2">
                {searchQuery ? 'Search Results' : 'All Tags'}
              </h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredTags.map(tag => (
                  <div
                    key={tag.id}
                    className="flex items-center justify-between p-3 bg-dark-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      <div>
                        <div className="font-medium text-white">{tag.name}</div>
                        {tag.description && (
                          <div className="text-sm text-gray-400">{tag.description}</div>
                        )}
                        <div className="text-xs text-gray-500">
                          Used {tag.usageCount} times
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingTag(tag)}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteConfirm({ type: 'tag', id: tag.id })}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="space-y-4">
            <div className="space-y-3">
              {categories.map(category => (
                <Card key={category.id} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      <h4 className="font-medium text-white">{category.name}</h4>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingCategory(category)}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteConfirm({ type: 'category', id: category.id })}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {category.tags.map(tagName => {
                      const tag = tags.find(t => t.name === tagName);
                      return (
                        <span
                          key={tagName}
                          className="px-2 py-1 text-xs rounded-full"
                          style={{ 
                            backgroundColor: tag ? `${tag.color}20` : '#374151',
                            color: tag?.color || '#9ca3af'
                          }}
                        >
                          {tagName}
                        </span>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Create Tag Modal */}
        {showCreateTag && (
          <Modal
            isOpen={true}
            onClose={() => setShowCreateTag(false)}
            title="Create New Tag"
          >
            <div className="space-y-4">
              <Input
                label="Tag Name"
                value={newTag.name}
                onChange={(e) => setNewTag(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter tag name..."
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Color
                </label>
                <ColorPicker
                  value={newTag.color}
                  onChange={(color) => setNewTag(prev => ({ ...prev, color }))}
                />
              </div>

              <Input
                label="Description (Optional)"
                value={newTag.description}
                onChange={(e) => setNewTag(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe this tag..."
              />

              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowCreateTag(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateTag}
                  disabled={!newTag.name.trim()}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Create Tag
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Edit Tag Modal */}
        {editingTag && (
          <Modal
            isOpen={true}
            onClose={() => setEditingTag(null)}
            title="Edit Tag"
          >
            <div className="space-y-4">
              <Input
                label="Tag Name"
                value={editingTag.name}
                onChange={(e) => setEditingTag(prev => prev ? { ...prev, name: e.target.value } : null)}
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Color
                </label>
                <ColorPicker
                  value={editingTag.color}
                  onChange={(color) => setEditingTag(prev => prev ? { ...prev, color } : null)}
                />
              </div>

              <Input
                label="Description (Optional)"
                value={editingTag.description || ''}
                onChange={(e) => setEditingTag(prev => prev ? { ...prev, description: e.target.value } : null)}
              />

              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setEditingTag(null)}
                >
                  Cancel
                </Button>
                <Button onClick={handleUpdateTag}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <Modal
            isOpen={true}
            onClose={() => setDeleteConfirm(null)}
            title={`Delete ${deleteConfirm.type === 'tag' ? 'Tag' : 'Category'}`}
          >
            <div className="space-y-4">
              <p className="text-gray-300">
                Are you sure you want to delete this {deleteConfirm.type}? 
                {deleteConfirm.type === 'tag' && ' It will be removed from all readings.'}
              </p>

              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteConfirm}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </Modal>
  );
}